import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";

export type ApiKeyListItem = {
  id: string;
  name: string;
  key: string;
  createdAt: string;
  lastUsedAt: string | null;
};

const API_KEYS_QUERY_KEY = ["apiKeys"] as const;

export function useApiKeys() {
  return useQuery({
    queryKey: API_KEYS_QUERY_KEY,
    queryFn: async () => {
      const res = await axiosInstance.get<ApiKeyListItem[]>("/api-keys");
      return res.data;
    },
  });
}

/** Not optimistic: the key itself only exists once the server makes it. */
export function useCreateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      const res = await axiosInstance.post<ApiKeyListItem>("/api-keys", { name });
      return res.data;
    },
    onSuccess: (created) => {
      queryClient.setQueryData<ApiKeyListItem[]>(API_KEYS_QUERY_KEY, (old) => [created, ...(old ?? [])]);
      queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY });
    },
    onError: () => {
      queue.add({ title: "Could not make the key", description: "Please try again.", variant: "error" });
    },
  });
}

export function useDeleteApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.delete(`/api-keys/${id}`);
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: API_KEYS_QUERY_KEY });
      const list = queryClient.getQueryData<ApiKeyListItem[]>(API_KEYS_QUERY_KEY);
      const index = list?.findIndex((k) => k.id === id) ?? -1;
      const removed = index >= 0 ? list![index] : null;
      queryClient.setQueryData<ApiKeyListItem[]>(API_KEYS_QUERY_KEY, (old) => (old ?? []).filter((k) => k.id !== id));
      return { removed, index };
    },
    onSuccess: (_data, _id, context) => {
      queue.add({ title: `Key “${context?.removed?.name ?? ""}” deleted`, variant: "success" });
    },
    onError: (_err, _id, context) => {
      if (context?.removed) {
        const { removed, index } = context;
        queryClient.setQueryData<ApiKeyListItem[]>(API_KEYS_QUERY_KEY, (old) => {
          const next = [...(old ?? [])];
          if (next.some((k) => k.id === removed.id)) return next;
          next.splice(Math.max(0, index), 0, removed);
          return next;
        });
      }
      queue.add({ title: "Could not delete the key", description: "It still works. Please try again.", variant: "error" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY });
    },
  });
}
